/**
 * Per-workflow canvas layout (node positions + dragged edge segments), kept in
 * localStorage since the backend WorkflowDefinition has no layout fields.
 *
 * Keys must survive a save/reload round-trip, but node ids don't: a freshly
 * dropped `node-123` comes back from the backend as step `step_node_123`, and a
 * data-source node comes back as `data_source_<input name>` (see
 * workflowToPipeline). So layout is keyed by the id each node WILL have after
 * reload, not by its current id.
 */

import { isDataSourceNode, sanitizeStepName } from '../api/client';
import type { EdgeRoute } from '../components/edges/MovableEdge';
import type { NodePosition, PipelineDefinition, PipelineNode } from '../types/pipeline';

export interface CanvasLayout {
  nodes: Record<string, NodePosition>;
  edges: Record<string, EdgeRoute>;
}

// Positions keep their pre-existing storage key so layouts saved before this
// module existed still load.
const positionsKey = (pipelineId: string) => `raiqon_workflow_positions_${pipelineId}`;
const routesKey = (pipelineId: string) => `raiqon_workflow_edge_routes_${pipelineId}`;

export function stableNodeKey(node: Pick<PipelineNode, 'id' | 'task_type' | 'config'>): string {
  if (isDataSourceNode(node as PipelineNode)) {
    // Same input name pipelineToWorkflow emits for this node.
    return `data_source_${node.config?.name || sanitizeStepName(node.id)}`;
  }
  return sanitizeStepName(node.id);
}

export function stableEdgeKey(
  sourceKey: string,
  targetKey: string,
  sourceHandle?: string | null
): string {
  return `${sourceKey}->${targetKey}${sourceHandle ? `:${sourceHandle}` : ''}`;
}

function readJson<T>(key: string): Record<string, T> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error(`Failed to load canvas layout (${key}):`, e);
    return {};
  }
}

export function loadCanvasLayout(pipelineId: string): CanvasLayout {
  return {
    nodes: readJson<NodePosition>(positionsKey(pipelineId)),
    edges: readJson<EdgeRoute>(routesKey(pipelineId)),
  };
}

export function saveCanvasLayout(pipelineId: string, layout: CanvasLayout): void {
  try {
    localStorage.setItem(positionsKey(pipelineId), JSON.stringify(layout.nodes));
    localStorage.setItem(routesKey(pipelineId), JSON.stringify(layout.edges));
  } catch (e) {
    console.error('Failed to save canvas layout:', e);
  }
}

/** Carry a layout over to a pipeline copy (fresh id, same node ids). */
export function copyCanvasLayout(from: PipelineDefinition, toId: string): void {
  saveCanvasLayout(toId, loadCanvasLayout(from.id));
}
